import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'src/components/layouts/Modal/Modal';
import SearchBar from 'src/components/ui/TableOverview/Components/SearchBar';
import OptionItemList from 'src/components/ui/OptionItemList/OptionItemList';
import { OptionItemAccessory, OptionItemTone } from 'src/components/ui/OptionItem/OptionItem';
import { Trigger, useWorkflowsTriggersQuery } from 'src/services/workflows';
import { getColorForTriggerCategory, getIconForTriggerCategory, getTriggerCategoryLabel } from 'src/utils/general';
import styles from './TriggerSelectionOverlay.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onTriggerSelect: (trigger: Trigger, groupLabel: string, model: string) => void;
};

type TriggerWithModel = { trigger: Trigger; model: string };

type CategoryGroup = {
  category: string;
  label: string;
  items: TriggerWithModel[];
};

const OVERVIEW_CATEGORY = '__overview__';

function groupTriggersByCategory(
  triggerGroupsData: ReturnType<typeof useWorkflowsTriggersQuery>['data'],
): CategoryGroup[] {
  if (!triggerGroupsData) return [];

  const categoryMap = new Map<string, TriggerWithModel[]>();

  for (const group of triggerGroupsData) {
    for (const trigger of group.items) {
      const cat = trigger.category || 'other';
      if (!categoryMap.has(cat)) {
        categoryMap.set(cat, []);
      }
      categoryMap.get(cat)!.push({ trigger, model: group.model });
    }
  }

  return Array.from(categoryMap.entries()).map(([category, items]) => ({
    category,
    label: getTriggerCategoryLabel(category),
    items,
  }));
}

export default function TriggerSelectionOverlay({ isOpen, onClose, onTriggerSelect }: Props) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>(OVERVIEW_CATEGORY);
  const [searchString, setSearchString] = useState('');

  const { data: triggerGroupsData } = useWorkflowsTriggersQuery();

  const categoryGroups = useMemo(() => groupTriggersByCategory(triggerGroupsData), [triggerGroupsData]);

  const filteredGroups = useMemo(() => {
    const query = searchString.trim().toLowerCase();
    const groups =
      selectedCategory === OVERVIEW_CATEGORY
        ? categoryGroups
        : categoryGroups.filter(g => g.category === selectedCategory);

    if (!query) return groups;

    return groups
      .map(group => ({
        ...group,
        items: group.items.filter(({ trigger }) => trigger.name.toLowerCase().includes(query)),
      }))
      .filter(group => group.items.length > 0);
  }, [categoryGroups, selectedCategory, searchString]);

  const handleClose = () => {
    setSearchString('');
    setSelectedCategory(OVERVIEW_CATEGORY);
    onClose();
  };

  const handleTriggerClick = (trigger: Trigger, groupLabel: string, model: string) => {
    onTriggerSelect(trigger, groupLabel, model);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} hasCloseButton>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className="typo-title-24">{t('workflows.trigger_overlay.title')}</h2>
          <p className="typo-text-14 text-faded mt-4">{t('workflows.trigger_overlay.subtitle')}</p>
        </div>

        <div className={styles.body}>
          <div className={styles.leftColumn}>
            <SearchBar value={searchString} onChange={setSearchString} placeholder={t('general.search_placeholder')} />

            <div className={styles.categoryList}>
              <button
                type="button"
                className={`${styles.categoryItem} ${selectedCategory === OVERVIEW_CATEGORY ? styles.active : ''}`}
                onClick={() => setSelectedCategory(OVERVIEW_CATEGORY)}>
                {t('workflows.trigger_overlay.overview')}
              </button>
              {categoryGroups.map(group => (
                <button
                  type="button"
                  key={group.category}
                  className={`${styles.categoryItem} ${selectedCategory === group.category ? styles.active : ''}`}
                  onClick={() => setSelectedCategory(group.category)}>
                  {group.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.rightColumn}>
            {filteredGroups.map(group => (
              <div key={group.category} className="pb-16">
                {selectedCategory === OVERVIEW_CATEGORY && (
                  <div className={styles.triggerGroupLabel}>{group.label}</div>
                )}
                {group.items.length > 0 && (
                  <OptionItemList
                    className="w-full"
                    triggerIcon
                    items={group.items.map(({ trigger, model }) => ({
                      id: trigger.id,
                      label: trigger.name,
                      subtitle: trigger.description || undefined,
                      accessoryItem: getIconForTriggerCategory(group.category),
                      accessoryBg: getColorForTriggerCategory(group.category),
                      tone: OptionItemTone.Default,
                      accessory: OptionItemAccessory.Thumbnail,
                      onClick: () => handleTriggerClick(trigger, group.label, model),
                    }))}
                  />
                )}
              </div>
            ))}

            {filteredGroups.length === 0 && (
              <div className="flex items-center justify-center h-full text-faded typo-text-14">
                {t('workflows.no_values')}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
